import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Filter, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import PropertyCard from '../components/PropertyCard';
import { api } from '../api';
import { useAuth } from '../contexts/AuthContext';

const PropertiesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { getAuthHeader, isAuthenticated } = useAuth();
  
  const [properties, setProperties] = useState([]);
  const [savedPropertyIds, setSavedPropertyIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  
  const [filters, setFilters] = useState({
    category: searchParams.get('category') || '',
    min_price: searchParams.get('min_price') || '',
    max_price: searchParams.get('max_price') || '',
    location: searchParams.get('location') || '',
    search: searchParams.get('search') || ''
  });

  useEffect(() => {
    loadProperties();
    if (isAuthenticated) {
      loadSavedProperties();
    }
  }, [searchParams, isAuthenticated]);

  const loadProperties = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.category) params.category = filters.category;
      if (filters.min_price) params.min_price = filters.min_price;
      if (filters.max_price) params.max_price = filters.max_price;
      if (filters.location) params.location = filters.location;
      if (filters.search) params.search = filters.search;

      const response = await api.getProperties(params);
      setProperties(response.data);
    } catch (error) {
      console.error('Error loading properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSavedProperties = async () => {
    try {
      const response = await api.getSavedProperties(getAuthHeader());
      setSavedPropertyIds(response.data.map(p => p.property_id));
    } catch (error) {
      console.error('Error loading saved properties:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
  };

  const applyFilters = () => {
    const params = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        params.set(key, filters[key]);
      }
    });
    setSearchParams(params);
  };

  const clearFilters = () => {
    setFilters({
      category: '',
      min_price: '',
      max_price: '',
      location: '',
      search: ''
    });
    setSearchParams(new URLSearchParams());
  };

  return (
    <div className="min-h-screen py-8" data-testid="properties-page">
      <div className="container mx-auto px-6 md:px-12">
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4 font-heading">
            Property Listings
          </h1>
          <p className="text-base md:text-lg text-slate-600">
            Browse {properties.length} properties from UK auction houses
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white border border-slate-200 rounded-sm p-6 mb-8" data-testid="filters-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filters
            </h2>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setShowFilters(!showFilters)}
              data-testid="toggle-filters-btn"
            >
              {showFilters ? 'Hide' : 'Show'}
            </Button>
          </div>

          {showFilters && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Category</label>
                  <Select 
                    value={filters.category} 
                    onValueChange={(value) => handleFilterChange('category', value)}
                  >
                    <SelectTrigger data-testid="category-filter">
                      <SelectValue placeholder="All Categories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">All Categories</SelectItem>
                      <SelectItem value="Residential">Residential</SelectItem>
                      <SelectItem value="Commercial">Commercial</SelectItem>
                      <SelectItem value="Land">Land</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Min Price</label>
                  <Input
                    type="number"
                    placeholder="e.g., 50000"
                    value={filters.min_price}
                    onChange={(e) => handleFilterChange('min_price', e.target.value)}
                    data-testid="min-price-filter"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Max Price</label>
                  <Input
                    type="number"
                    placeholder="e.g., 500000"
                    value={filters.max_price}
                    onChange={(e) => handleFilterChange('max_price', e.target.value)}
                    data-testid="max-price-filter"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Location</label>
                  <Input
                    type="text"
                    placeholder="e.g., London, Manchester"
                    value={filters.location}
                    onChange={(e) => handleFilterChange('location', e.target.value)}
                    data-testid="location-filter"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Search</label>
                  <Input
                    type="text"
                    placeholder="Keywords..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    data-testid="search-filter"
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <Button onClick={applyFilters} data-testid="apply-filters-btn">
                  Apply Filters
                </Button>
                <Button variant="outline" onClick={clearFilters} data-testid="clear-filters-btn">
                  Clear All
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Properties Grid */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-slate-600">Loading properties...</p>
          </div>
        ) : properties.length === 0 ? (
          <div className="text-center py-16 bg-slate-50 rounded-2xl border border-slate-200">
            <div className="max-w-md mx-auto px-4">
              <Search className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No properties found</h3>
              <p className="text-slate-600 mb-6 text-sm">
                Try increasing your price range, changing location, or adjusting your filters to see more results.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={clearFilters} variant="outline" data-testid="no-results-clear-btn">
                  Clear All Filters
                </Button>
                <Button onClick={() => window.location.href = '/properties'} className="bg-[#F5A623] hover:bg-[#E59512]">
                  View All Properties
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {properties.map((property) => (
              <PropertyCard 
                key={property.property_id} 
                property={property}
                isSaved={savedPropertyIds.includes(property.property_id)}
                onSaveToggle={loadSavedProperties}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertiesPage;
